import { cloneDeep, sum } from 'lodash';
import { v4 as uuid } from 'uuid';
import { Message } from '@meitu/saas-ui';
import {
    convertMaterialIdsToMap,
    convertMaterialIdsToMapRecursive,
    convertMaterialIdsToObjRecursive,
    deepDeletePrivateProps,
    getFramework,
    getTrackFunc,
} from '@/framework';
import { getLocaleStore } from '@/i18n';
import { IAddLayerArgs } from '@/layers/layer';
import { CellsSelectedInfo, getTableEditState } from '@/plugins/table-edit/state';
import addOrUpdateLayer, { resetTableLayerMaterialIds } from '@/plugins/table-edit/utils/add-or-update-layer';
import { getLayerViewNode } from '@/utils/layer-kit';
import { Border, Cell, ILayerProps, ITableLayer, RowOrColSelectedRectInfo } from '@/version-types';
import { getTableLayerScreenshotUrl } from '../behaviors/save';
import { MAX_COL_COUNT, MAX_ROW_COUNT } from '../default-configs';
import { SAAS_EDITOR_TABLE_CLASS_NAME } from '../view/table-layer';
import fixTableLayout from './fix-table-layout';
import getTableLayerByXLSX, { getScaledTableLayerByStageSize, getUsingStyles } from './get-table-layer-by-xlsx';
import { getCellStyle } from './table';

const CUSTOM_CLIPBOARD_DATA_KEY = 'custom-clipboard-data';

interface SelectedInfo {
    cellsSelectedInfo: CellsSelectedInfo | null;
    rowOrColSelectedRectInfo: RowOrColSelectedRectInfo | null;
}

interface ICustomClipboardData {
    stageSize: {
        width: number;
        height: number;
    };
    layer: ITableLayer<true>;
    selectedInfo: {
        cellsSelectedInfo: CellsSelectedInfo | null;
        rowOrColSelectedRectInfo: RowOrColSelectedRectInfo | null;
    };
}

const getCustomClipboardData = (clipboardHTML: string): ICustomClipboardData | null => {
    const regexp = new RegExp(`data-${CUSTOM_CLIPBOARD_DATA_KEY}=['"]([^'"]+)['"]`);
    const customClipboardDataText = clipboardHTML.match(regexp)?.[1];
    try {
        if (customClipboardDataText) {
            const customClipboardData = JSON.parse(
                decodeURIComponent(atob(customClipboardDataText)),
            ) as ICustomClipboardData;
            customClipboardData.layer.id = uuid();
            convertMaterialIdsToMapRecursive([customClipboardData.layer as any as ILayerProps]);
            return customClipboardData;
        }
        return null;
    } catch {
        return null;
    }
};

/** 获取表格图层选区对应的剪切板数据 */
export const getTableClipboardData = async ({
    frameworkIdentity,
    layerId,
    selectedInfo,
}: {
    frameworkIdentity: string;
    layerId: string;
    selectedInfo: SelectedInfo;
}) => {
    const { cellsSelectedInfo, rowOrColSelectedRectInfo } = selectedInfo;
    const hasSelectedInfo = !!cellsSelectedInfo || !!rowOrColSelectedRectInfo;
    if (!hasSelectedInfo) return null;

    const framework = getFramework(frameworkIdentity);
    const stageSize = framework.getSizeByIndex(framework.stage.getActiveStageIndex());
    const layer = framework.getLayer(layerId) as ITableLayer | null;
    if (!layer) return null;

    const tableNode = getLayerViewNode(frameworkIdentity, layerId)?.querySelector<HTMLDivElement>(
        `.${SAAS_EDITOR_TABLE_CLASS_NAME}`,
    );
    if (!tableNode) return null;

    const { table_to_sheet, sheet_to_txt } = (await import('xlsx')).utils;

    const tempContainer = document.createElement('div');
    const tableNodeClassName = `table-${uuid()}`;
    const tempTableNode = tableNode.cloneNode(true) as HTMLDivElement;
    document.body.appendChild(tempContainer);
    tempContainer.appendChild(tempTableNode);
    tempTableNode.classList.add(tableNodeClassName);

    let customClipboardData: ICustomClipboardData;
    // 存入自定义数据, 用于粘贴回编辑器时完全复刻
    {
        const _layer = cloneDeep(layer);
        convertMaterialIdsToObjRecursive([_layer as any as ILayerProps]);
        deepDeletePrivateProps(_layer);
        customClipboardData = {
            layer: _layer as any as ITableLayer<true>,
            selectedInfo: selectedInfo as SelectedInfo,
            stageSize,
        };
        tempTableNode.setAttribute(
            `data-${CUSTOM_CLIPBOARD_DATA_KEY}`,
            btoa(encodeURIComponent(JSON.stringify(customClipboardData))),
        );
    }

    // 表格图层的边框在svg上, 需要把table的边框尽量还原出来
    {
        tempTableNode.querySelector('svg')?.remove();
        const styleNode = document.createElement('style');
        styleNode.textContent = `
          .${tableNodeClassName} td {
              border-color: inherit !important;
          }
      `;
        tempTableNode.appendChild(styleNode);
    }

    // 只留下选区
    {
        const startHIndex = rowOrColSelectedRectInfo
            ? rowOrColSelectedRectInfo.direction === 'vertical'
                ? rowOrColSelectedRectInfo.index
                : 0
            : cellsSelectedInfo!.rect.startHIndex;
        const startVIndex = rowOrColSelectedRectInfo
            ? rowOrColSelectedRectInfo.direction === 'horizontal'
                ? rowOrColSelectedRectInfo.index
                : 0
            : cellsSelectedInfo!.rect.startVIndex;
        const endHIndex = rowOrColSelectedRectInfo
            ? rowOrColSelectedRectInfo.direction === 'vertical'
                ? rowOrColSelectedRectInfo.index + 1
                : layer.data.baseCells.horizontal.length
            : cellsSelectedInfo!.rect.endHIndex;
        const endVIndex = rowOrColSelectedRectInfo
            ? rowOrColSelectedRectInfo.direction === 'horizontal'
                ? rowOrColSelectedRectInfo.index + 1
                : layer.data.baseCells.vertical.length
            : cellsSelectedInfo!.rect.endVIndex;
        const colEls = Array.from(tempTableNode.querySelectorAll('col'));
        const trEls = Array.from(tempTableNode.querySelectorAll('tr'));
        // 删除行
        trEls.forEach((tr, index) => {
            if (index < startVIndex || index >= endVIndex) {
                tr.remove();
            }
        });
        // 删除列
        colEls.forEach((col, index) => {
            if (index < startHIndex || index >= endHIndex) {
                col.remove();
            }
        });
        trEls.forEach((tr) => {
            const tdEls = Array.from(tr.querySelectorAll('td'));
            tdEls.forEach((td, index) => {
                if (index < startHIndex || index >= endHIndex) {
                    td.remove();
                }
            });
        });
    }

    {
        if (rowOrColSelectedRectInfo) {
            // TODO: 如果选中的单元格是半个合并单元格的话, 需要处理, 不处理的话粘贴到xlsx或其他地方的布局会不一致
        }
    }

    // display: none的td会干扰xlsx的解析, 需要去除
    {
        const tdEls = Array.from(tempTableNode.querySelectorAll('td'));
        tdEls.forEach((td) => {
            if (td.style.display === 'none') {
                td.remove();
            }
        });
    }

    const worksheet = table_to_sheet(tempTableNode);
    const clipboardText = sheet_to_txt(worksheet);
    const clipboardHtml = tempTableNode.outerHTML;

    document.body.removeChild(tempContainer);

    console.log('===表格写入剪切板的内容', {
        clipboardText,
        clipboardHtml,
        customClipboardData,
    });

    const clipboardData = [
        new ClipboardItem({
            ['text/plain']: new Blob([clipboardText], { type: 'text/plain' }),
            ['text/html']: new Blob([clipboardHtml], { type: 'text/html' }),
        }),
    ];

    return clipboardData;
};

/**
 * 剪切板数据转化处理后的tableLayer
 * 返回的tableLayer是不带screenshotUrl的, 需要额外手动生成
 */
const getTableLayerByClipboardData = async ({
    frameworkIdentity,
    clipboardText,
    clipboardHTML,
    noFixTableLayout = false,
    onExceed,
}: {
    frameworkIdentity: string;
    clipboardText: string;
    clipboardHTML?: string | null;
    /** 是否需要修复表格布局, 默认为修复 */
    noFixTableLayout?: boolean;
    /** 如果剪切板中的表格数据超出表格图层行列限制, 则回调 */
    onExceed?: () => void;
}): Promise<ITableLayer<true>> => {
    const customClipboardData = clipboardHTML ? getCustomClipboardData(clipboardHTML) : null;
    const framework = getFramework(frameworkIdentity);
    const stageSize = framework.getSizeByIndex(framework.stage.getActiveStageIndex());
    const stageInfo = {
        width: stageSize.width,
        height: stageSize.height,
        scale: framework.stage.getScale(),
    };
    if (customClipboardData) {
        let newLayer = customClipboardData.layer;
        convertMaterialIdsToMap(newLayer as any as ILayerProps);
        const scale = Math.min(
            customClipboardData.stageSize.width / stageSize.width,
            customClipboardData.stageSize.height / stageSize.height,
        );
        newLayer = getScaledTableLayerByStageSize(newLayer, scale);
        // 按选中区域裁剪
        {
            const { cellsSelectedInfo, rowOrColSelectedRectInfo } = customClipboardData.selectedInfo;
            let rect = {
                startHIndex: 0,
                startVIndex: 0,
                endHIndex: newLayer.data.baseCells.horizontal.length,
                endVIndex: newLayer.data.baseCells.vertical.length,
            };
            const hasSelectedInfo = !!cellsSelectedInfo || !!rowOrColSelectedRectInfo;
            if (hasSelectedInfo) {
                if (cellsSelectedInfo) {
                    rect = cellsSelectedInfo.rect;
                } else {
                    const startHIndex =
                        rowOrColSelectedRectInfo!.direction === 'vertical' ? rowOrColSelectedRectInfo!.index : 0;
                    const startVIndex =
                        rowOrColSelectedRectInfo!.direction === 'horizontal' ? rowOrColSelectedRectInfo!.index : 0;
                    const endHIndex =
                        rowOrColSelectedRectInfo!.direction === 'vertical'
                            ? rowOrColSelectedRectInfo!.index + 1
                            : newLayer.data.baseCells.horizontal.length;
                    const endVIndex =
                        rowOrColSelectedRectInfo!.direction === 'horizontal'
                            ? rowOrColSelectedRectInfo!.index + 1
                            : newLayer.data.baseCells.vertical.length;
                    rect = {
                        startHIndex,
                        startVIndex,
                        endHIndex,
                        endVIndex,
                    };
                }
            }
            const newHSizes = newLayer.data.baseCells.horizontal.slice(rect.startHIndex, rect.endHIndex);
            const newVSizes = newLayer.data.baseCells.vertical.slice(rect.startVIndex, rect.endVIndex);
            newLayer.width -= sum(newLayer.data.baseCells.horizontal) - sum(newHSizes);
            newLayer.height -= sum(newLayer.data.baseCells.vertical) - sum(newVSizes);
            newLayer.data.baseCells.horizontal = newHSizes;
            newLayer.data.baseCells.vertical = newVSizes;
            if (rowOrColSelectedRectInfo) {
                // 被选区分割的合并单元格
                const slicedCells = (newLayer.data.cells.flat().filter(Boolean) as Cell[]).filter(
                    ({ hIndex, vIndex, colSpan, rowSpan }) =>
                        (hIndex < rect.startHIndex && hIndex + colSpan > rect.startHIndex) ||
                        (hIndex < rect.endHIndex && hIndex + colSpan > rect.endHIndex) ||
                        (vIndex < rect.startVIndex && vIndex + rowSpan > rect.startVIndex) ||
                        (vIndex < rect.endVIndex && vIndex + rowSpan > rect.endVIndex),
                );
                // 如果选中的单元格是被分割的合并单元格的话, 需要处理, 不处理的话会有很多bug, 比如边框不正常
                const doCell = (cell: Cell) => {
                    const isInSelectedRect = cell.hIndex >= rect.startHIndex && cell.vIndex >= rect.startVIndex;
                    if (isInSelectedRect) {
                        // 如果合并单元格的左上角在区域内 -> 剩余部分收敛到区域中
                        if (cell.hIndex + cell.colSpan > rect.endHIndex) {
                            cell.colSpan = rect.endHIndex - cell.hIndex;
                        }
                        if (cell.vIndex + cell.rowSpan > rect.endVIndex) {
                            cell.rowSpan = rect.endVIndex - cell.vIndex;
                        }
                    } else {
                        // 如果合并单元格的左上角不在区域内 -> 创建一个样式相同的包含之前所占区域的合并单元格
                        const newCell: Cell = cloneDeep(cell);
                        if (newCell.hIndex < rect.startHIndex) {
                            newCell.hIndex = rect.startHIndex;
                        }
                        if (newCell.vIndex < rect.startVIndex) {
                            newCell.vIndex = rect.startVIndex;
                        }
                        newCell.colSpan -= cell.hIndex - newCell.hIndex;
                        newCell.rowSpan -= cell.vIndex - newCell.vIndex;
                        if (newCell.hIndex + newCell.colSpan > rect.endHIndex) {
                            newCell.colSpan = rect.endHIndex - newCell.hIndex;
                        }
                        if (newCell.vIndex + newCell.rowSpan > rect.endVIndex) {
                            newCell.rowSpan = rect.endVIndex - newCell.vIndex;
                        }
                        newLayer.data.cells[newCell.vIndex][newCell.hIndex] = newCell;
                    }
                };
                slicedCells.forEach(doCell);
            }
            newLayer.data.cells = newLayer.data.cells
                .slice(rect.startVIndex, rect.endVIndex)
                .map((row) => row.slice(rect.startHIndex, rect.endHIndex));
            // hIndex和vIndex需要修改, 从0开始
            newLayer.data.cells.flat().forEach((cell) => {
                if (cell) {
                    cell.hIndex -= rect.startHIndex;
                    cell.vIndex -= rect.startVIndex;
                }
            });
            newLayer.data.styles = getUsingStyles(newLayer);
            if (!noFixTableLayout) {
                newLayer = await fixTableLayout({
                    originalLayer: newLayer,
                    stageScale: stageInfo.scale,
                });
            }
        }
        resetTableLayerMaterialIds(frameworkIdentity, newLayer as any as ITableLayer);
        delete newLayer.data.screenshotUrl;
        return newLayer;
    }

    const xlsx = await import('xlsx');
    const { Sheets, SheetNames } = xlsx.read(clipboardHTML || clipboardText, {
        type: 'string',
        // 保留日期对象
        cellDates: true,
    });
    const worksheet = Sheets[SheetNames[0]];
    const { tableLayer, isExceed } = await getTableLayerByXLSX(frameworkIdentity, { worksheet }, stageInfo);
    if (isExceed) {
        onExceed?.();
    }
    const newLayer = cloneDeep(tableLayer);
    resetTableLayerMaterialIds(frameworkIdentity, newLayer);
    delete newLayer.data.screenshotUrl;
    return newLayer;
};

const getClipboardData = async (clipboard: ClipboardItem) => {
    let clipboardText = '';
    let clipboardHTML: string | null = null;
    let customClipboardData: ICustomClipboardData | null = null;
    if (clipboard.types.includes('text/plain')) {
        const blob = await clipboard.getType('text/plain');
        clipboardText = await blob.text();
    }
    if (clipboard.types.includes('text/html')) {
        const blob = await clipboard.getType('text/html');
        clipboardHTML = await blob.text();
        // 不知道为什么, 这么拿到的html会没有<html>标签, 所以手动加上
        if (clipboardHTML && !clipboardHTML.trimStart().startsWith('<html')) {
            clipboardHTML = `<html>${clipboardHTML}</html>`;
        }
        customClipboardData = getCustomClipboardData(clipboardHTML);
    }
    return {
        clipboardText,
        clipboardHTML,
        customClipboardData,
    };
};

export const pasteToTableLayer = async ({
    frameworkIdentity,
    layerId,
    cellsSelectedInfo,
}: {
    frameworkIdentity: string;
    layerId: string;
    cellsSelectedInfo: CellsSelectedInfo;
}) => {
    const clipboard = (await navigator.clipboard.read())[0];
    if (!clipboard) return;
    const framework = getFramework(frameworkIdentity);
    const targetLayer = framework.getLayer(layerId) as ITableLayer | undefined;
    if (!targetLayer) return;

    const { clipboardText, clipboardHTML, customClipboardData } = await getClipboardData(clipboard);
    const clipboardLayer = await getTableLayerByClipboardData({
        frameworkIdentity,
        clipboardText,
        clipboardHTML,
        noFixTableLayout: true,
    });
    console.log('===pasteToTableLayer', {
        clipboardText,
        clipboardHTML,
        customClipboardData,
        clipboardLayer: cloneDeep(clipboardLayer),
    });

    let newLayer = cloneDeep(targetLayer);
    const { startHIndex, startVIndex } = cellsSelectedInfo.rect;

    // layout
    {
        clipboardLayer.data.baseCells.horizontal.forEach((clipboardHSize, clipboardHIndex) => {
            const hIndex = startHIndex + clipboardHIndex;
            if (hIndex > newLayer.data.baseCells.horizontal.length - 1) {
                newLayer.data.baseCells.horizontal.push(clipboardHSize);
                newLayer.width += clipboardHSize;
            } else if (!clipboardHTML) {
                // 从clipboardText粘贴过来的, 不要去动原本的layout, 因为clipboardText本身就是不带layout信息的
                newLayer.data.baseCells.horizontal[hIndex] = clipboardHSize;
                newLayer.width += clipboardHSize - newLayer.data.baseCells.horizontal[hIndex];
            }
        });
        clipboardLayer.data.baseCells.vertical.forEach((clipboardVSize, clipboardVIndex) => {
            const vIndex = startVIndex + clipboardVIndex;
            if (vIndex > newLayer.data.baseCells.vertical.length - 1) {
                newLayer.data.baseCells.vertical.push(clipboardVSize);
                newLayer.height += clipboardVSize;
            } else if (!clipboardHTML) {
                // 从clipboardText粘贴过来的, 不要去动原本的layout, 因为clipboardText本身就是不带layout信息的
                newLayer.data.baseCells.vertical[vIndex] = clipboardVSize;
                newLayer.height += clipboardVSize - newLayer.data.baseCells.vertical[vIndex];
            }
        });
    }

    // 将除了border外的styles加到newLayer
    let styleIdDiff: number = 0;
    let clipboardAllStyleId: string = '';
    {
        // 架空clipboardLayer的allStyleId属性, 这样直接合并styles和cell.styleIds就不会漏掉样式
        clipboardLayer.data.cells.flat().forEach((cell) => {
            if (cell) {
                cell.styleIds = [clipboardLayer.data.allStyleId, ...cell.styleIds];
            }
        });

        // 去掉clipboardLayer的边框样式
        const deleteStyleIds: string[] = [];
        for (const styleId in clipboardLayer.data.styles) {
            delete clipboardLayer.data.styles[styleId].border;
            if (
                Object.keys(clipboardLayer.data.styles[styleId]).length === 0 &&
                styleId !== clipboardLayer.data.allStyleId
            ) {
                deleteStyleIds.push(styleId);
                delete clipboardLayer.data.styles[styleId];
            }
        }
        clipboardLayer.data.cells.flat().forEach((cell) => {
            if (cell) {
                cell.styleIds = cell.styleIds.filter((styleId) => !deleteStyleIds.includes(styleId));
            }
        });

        // 保证styleId不重复, 能直接覆盖
        const layerMaxStyleId = Math.max(...Object.keys(newLayer.data.styles).map(Number), +newLayer.data.allStyleId);
        const clipboardMinStyleId = Math.min(
            ...Object.keys(clipboardLayer.data.styles).map(Number),
            +clipboardLayer.data.allStyleId,
        );
        styleIdDiff = clipboardMinStyleId <= layerMaxStyleId ? 1 + layerMaxStyleId - clipboardMinStyleId : 0;
        clipboardAllStyleId = `${+clipboardLayer.data.allStyleId + styleIdDiff}`;
        clipboardLayer.data.allStyleId = clipboardAllStyleId;
        clipboardLayer.data.styles = Object.fromEntries(
            Object.entries(clipboardLayer.data.styles).map(([styleId, style]) => [
                Number(styleId) + styleIdDiff,
                style,
            ]),
        );
        clipboardLayer.data.cells.flat().forEach((cell) => {
            if (cell) {
                cell.styleIds = cell.styleIds.map((styleId) => `${+styleId + styleIdDiff}`);
            }
        });

        newLayer.data.styles = {
            ...newLayer.data.styles,
            ...cloneDeep(clipboardLayer.data.styles),
        };
    }

    // cells
    {
        // 1. 统计与覆盖区域有交集的合并单元格
        const overlapArea = {
            startHIndex,
            startVIndex,
            endHIndex: startHIndex + clipboardLayer.data.baseCells.horizontal.length,
            endVIndex: startVIndex + clipboardLayer.data.baseCells.vertical.length,
        };
        const checkCellIsMerged = (cell: Record<'colSpan' | 'rowSpan', number>): boolean =>
            cell.colSpan > 1 || cell.rowSpan > 1;
        const checkIsIntersected = (cell: Cell): boolean => {
            const hIsIntersected =
                cell.hIndex + cell.colSpan > overlapArea.startHIndex && cell.hIndex < overlapArea.endHIndex;
            const vIsIntersected =
                cell.vIndex + cell.rowSpan > overlapArea.startVIndex && cell.vIndex < overlapArea.endVIndex;
            return hIsIntersected && vIsIntersected;
        };
        const mergedCellInfos: { cell: Cell; isInOverlapArea: boolean }[] = [];
        (newLayer.data.cells.flat().filter(Boolean) as Cell[])
            .filter((c) => checkCellIsMerged(c) && checkIsIntersected(c))
            .forEach((cell) => {
                const isInOverlapArea =
                    cell.hIndex >= overlapArea.startHIndex && cell.vIndex >= overlapArea.startVIndex;
                mergedCellInfos.push({ cell, isInOverlapArea });
            });

        // 2. 处理合并单元格, 该补充单元格的补充, 该修改位置/范围的修改
        const emptyCellPositions: { hIndex: number; vIndex: number; styleIds: string[] }[] = [];
        mergedCellInfos.forEach(({ cell, isInOverlapArea }) => {
            const originalCell = cloneDeep(cell);
            // isInOverlapArea就是在里面, 就需要修改位置/范围; 否则得换范围
            if (isInOverlapArea) {
                const isShouldAppendEmptyCell =
                    overlapArea.endVIndex < cell.vIndex + cell.rowSpan &&
                    overlapArea.endHIndex < cell.hIndex + cell.colSpan;
                if (overlapArea.endVIndex > cell.vIndex && overlapArea.endVIndex < cell.vIndex + cell.rowSpan) {
                    const originalCStartVIndex = cell.vIndex;
                    const originalCVEndIndex = cell.vIndex + cell.rowSpan;
                    cell.vIndex = overlapArea.endVIndex;
                    cell.rowSpan = originalCVEndIndex - overlapArea.endVIndex;
                    if (isShouldAppendEmptyCell) {
                        for (let i = originalCStartVIndex; i < cell.vIndex; i++) {
                            for (let j = overlapArea.endHIndex; j < cell.hIndex + cell.colSpan; j++) {
                                emptyCellPositions.push({
                                    hIndex: j,
                                    vIndex: i,
                                    styleIds: [...cell.styleIds],
                                });
                            }
                        }
                    }
                }
                if (overlapArea.endHIndex > cell.hIndex && overlapArea.endHIndex < cell.hIndex + cell.colSpan) {
                    const originalCStartHIndex = cell.hIndex;
                    const originalCHEndIndex = cell.hIndex + cell.colSpan;
                    cell.hIndex = overlapArea.endHIndex;
                    cell.colSpan = originalCHEndIndex - overlapArea.endHIndex;
                    if (isShouldAppendEmptyCell) {
                        for (let i = originalCStartHIndex; i < cell.hIndex; i++) {
                            for (let j = overlapArea.endVIndex; j < cell.vIndex + cell.rowSpan; j++) {
                                emptyCellPositions.push({
                                    hIndex: i,
                                    vIndex: j,
                                    styleIds: [...cell.styleIds],
                                });
                            }
                        }
                    }
                }
                if (originalCell.hIndex !== cell.hIndex || originalCell.vIndex !== cell.vIndex) {
                    newLayer.data.cells[originalCell.vIndex][originalCell.hIndex] = null;
                    newLayer.data.cells[cell.vIndex][cell.hIndex] = cell;
                }
            } else {
                if (overlapArea.startVIndex > cell.vIndex) {
                    cell.rowSpan = overlapArea.startVIndex - cell.vIndex;
                    for (let i = overlapArea.startVIndex; i < originalCell.vIndex + originalCell.rowSpan; i++) {
                        for (let j = overlapArea.endHIndex; j < originalCell.hIndex + originalCell.colSpan; j++) {
                            emptyCellPositions.push({
                                hIndex: j,
                                vIndex: i,
                                styleIds: [...cell.styleIds],
                            });
                        }
                    }
                    for (let i = overlapArea.endVIndex; i < originalCell.vIndex + originalCell.rowSpan; i++) {
                        for (
                            let j = originalCell.hIndex;
                            j < Math.min(originalCell.hIndex + originalCell.colSpan, overlapArea.endHIndex);
                            j++
                        ) {
                            emptyCellPositions.push({
                                hIndex: j,
                                vIndex: i,
                                styleIds: [...cell.styleIds],
                            });
                        }
                    }
                }
                if (overlapArea.startHIndex > cell.hIndex) {
                    cell.colSpan = overlapArea.startHIndex - cell.hIndex;
                    for (let i = overlapArea.startHIndex; i < originalCell.hIndex + originalCell.colSpan; i++) {
                        for (let j = overlapArea.endVIndex; j < originalCell.vIndex + originalCell.rowSpan; j++) {
                            emptyCellPositions.push({
                                hIndex: i,
                                vIndex: j,
                                styleIds: [...cell.styleIds],
                            });
                        }
                    }
                    for (let i = overlapArea.endHIndex; i < originalCell.hIndex + originalCell.colSpan; i++) {
                        for (
                            let j = originalCell.vIndex;
                            j < Math.min(originalCell.vIndex + originalCell.rowSpan, overlapArea.endVIndex);
                            j++
                        ) {
                            emptyCellPositions.push({
                                hIndex: i,
                                vIndex: j,
                                styleIds: [...cell.styleIds],
                            });
                        }
                    }
                }
            }
        });
        emptyCellPositions.forEach(({ hIndex, vIndex, styleIds }) => {
            newLayer.data.cells[vIndex][hIndex] = {
                __renderId: uuid(),
                hIndex,
                vIndex,
                colSpan: 1,
                rowSpan: 1,
                content: '',
                styleIds,
            };
        });

        // 3. 覆盖cell
        const newHIndexes: number[] = [];
        const newVIndexes: number[] = [];
        clipboardLayer.data.cells.forEach((row, clipboardVIndex) => {
            row.forEach((clipboardCell, clipboardHIndex) => {
                const hIndex = startHIndex + clipboardHIndex;
                const vIndex = startVIndex + clipboardVIndex;
                if (!newLayer.data.cells[vIndex]) {
                    newLayer.data.cells[vIndex] = [];
                    if (!newVIndexes.includes(vIndex)) {
                        newVIndexes.push(vIndex);
                    }
                }
                if (hIndex >= targetLayer.data.baseCells.horizontal.length && !newHIndexes.includes(hIndex)) {
                    newHIndexes.push(hIndex);
                }
                if (clipboardCell === null) {
                    newLayer.data.cells[vIndex][hIndex] = null;
                    return;
                }
                let cell = newLayer.data.cells[vIndex][hIndex];
                if (!cell) {
                    cell = {
                        __renderId: uuid(),
                        hIndex: NaN,
                        vIndex: NaN,
                        colSpan: NaN,
                        rowSpan: NaN,
                        content: '',
                        styleIds: [],
                    };
                    newLayer.data.cells[vIndex][hIndex] = cell;
                }
                cell.hIndex = hIndex;
                cell.vIndex = vIndex;
                cell.colSpan = clipboardCell.colSpan;
                cell.rowSpan = clipboardCell.rowSpan;
                cell.content = clipboardCell.content;
                cell.styleIds = [...cell.styleIds, clipboardAllStyleId, ...clipboardCell.styleIds];
            });
        });

        // 4. 新增的行列补全空白单元格
        const fixCell = (hIndex: number, vIndex: number) => {
            if (typeof newLayer.data.cells[vIndex][hIndex] !== 'undefined') return;
            const isInMergeCell = newLayer.data.cells
                .flat()
                .some(
                    (c) =>
                        c &&
                        c.hIndex <= hIndex &&
                        c.vIndex <= vIndex &&
                        c.hIndex + c.colSpan > hIndex &&
                        c.vIndex + c.rowSpan > vIndex,
                );
            newLayer.data.cells[vIndex][hIndex] = isInMergeCell
                ? null
                : {
                      __renderId: uuid(),
                      hIndex,
                      vIndex,
                      colSpan: 1,
                      rowSpan: 1,
                      content: '',
                      styleIds: [],
                  };
        };
        newHIndexes.forEach((hIndex) => {
            newLayer.data.baseCells.vertical.forEach((_, vIndex) => {
                fixCell(hIndex, vIndex);
            });
        });
        newVIndexes.forEach((vIndex) => {
            newLayer.data.baseCells.horizontal.forEach((_, hIndex) => {
                fixCell(hIndex, vIndex);
            });
        });
    }

    // 填充边框
    {
        const originalCells = targetLayer.data.cells.flat().filter(Boolean) as Cell[];
        const firstOriginalCellBorder = getCellStyle(originalCells[0], targetLayer.data).border!;
        const getOriginalBorderByPosition = (
            hIndex: number,
            vIndex: number,
            mode: 'horizontal' | 'vertical',
        ): Border => {
            let border: Border;
            if (mode === 'vertical') {
                const rightCell = originalCells.findLast(
                    (c) => c.hIndex === hIndex && c.vIndex <= vIndex && c.vIndex + c.rowSpan > vIndex,
                );
                const leftCell = originalCells.findLast(
                    (c) => c.hIndex + c.colSpan === hIndex && c.vIndex <= vIndex && c.vIndex + c.rowSpan > vIndex,
                );
                if (rightCell) {
                    const maybeBorder = getCellStyle(rightCell, targetLayer.data).border?.left;
                    maybeBorder && (border = maybeBorder);
                } else if (leftCell) {
                    const maybeBorder = getCellStyle(leftCell, targetLayer.data).border?.right;
                    maybeBorder && (border = maybeBorder);
                }
                if (!border!) {
                    border = firstOriginalCellBorder.left;
                }
            } else {
                const bottomCell = originalCells.findLast(
                    (c) => c.vIndex === vIndex && c.hIndex <= hIndex && c.hIndex + c.colSpan > hIndex,
                );
                const topCell = originalCells.findLast(
                    (c) => c.vIndex + c.rowSpan === vIndex && c.hIndex <= hIndex && c.hIndex + c.colSpan > hIndex,
                );
                if (bottomCell) {
                    const maybeBorder = getCellStyle(bottomCell, targetLayer.data).border?.top;
                    maybeBorder && (border = maybeBorder);
                } else if (topCell) {
                    const maybeBorder = getCellStyle(topCell, targetLayer.data).border?.bottom;
                    maybeBorder && (border = maybeBorder);
                }
                if (!border!) {
                    border = firstOriginalCellBorder.top;
                }
            }
            return border;
        };
        const originalEndHIndex = targetLayer.data.baseCells.horizontal.length;
        const originalEndVIndex = targetLayer.data.baseCells.vertical.length;
        const originalRightSideBorder = getOriginalBorderByPosition(
            originalEndHIndex,
            originalEndVIndex - 1,
            'vertical',
        );
        const originalBottomSideBorder = getOriginalBorderByPosition(
            originalEndHIndex - 1,
            originalEndVIndex,
            'horizontal',
        );
        (newLayer.data.cells.flat().filter(Boolean) as Cell[]).forEach((cell) => {
            if (cell.hIndex >= originalEndHIndex || cell.vIndex >= originalEndVIndex) {
                const getNewStyleId = () => `${Math.max(...Object.keys(newLayer.data.styles).map(Number)) + 1}`;
                const newStyleId = getNewStyleId();
                cell.styleIds.push(newStyleId);
                const border: Record<'top' | 'left' | 'right' | 'bottom', Border> = {} as any;
                if (cell.hIndex >= originalEndHIndex) {
                    border.left = originalRightSideBorder;
                    border.right = originalRightSideBorder;
                } else {
                    border.left = getOriginalBorderByPosition(cell.hIndex, cell.vIndex, 'vertical');
                    border.right = getOriginalBorderByPosition(cell.hIndex + cell.colSpan, cell.vIndex, 'vertical');
                }
                if (cell.vIndex >= originalEndVIndex) {
                    border.top = originalBottomSideBorder;
                    border.bottom = originalBottomSideBorder;
                } else {
                    border.top = getOriginalBorderByPosition(cell.hIndex, cell.vIndex, 'horizontal');
                    border.bottom = getOriginalBorderByPosition(cell.hIndex, cell.vIndex + cell.rowSpan, 'horizontal');
                }
                newLayer.data.styles[newStyleId] = { border };
            }
        });
    }

    const checkLayerIsDirty = (layer: ITableLayer): boolean => {
        let isDirty = false;
        if (
            layer.data.cells.flat().length !==
            layer.data.baseCells.horizontal.length * layer.data.baseCells.vertical.length
        ) {
            console.log('===表格粘贴异常: cell count dirty');
            isDirty = true;
        }
        const mergedCells = (layer.data.cells.flat().filter(Boolean) as Cell[]).filter(
            (c) => c.colSpan > 1 || c.rowSpan > 1,
        );
        layer.data.cells.forEach((row, vIndex) => {
            row.forEach((cell, hIndex) => {
                if (cell) {
                    if (cell.hIndex !== hIndex || cell.vIndex !== vIndex) {
                        console.log('===表格粘贴异常: cell position dirty', { hIndex, vIndex });
                        isDirty = true;
                    }
                } else {
                    const isInMerged = mergedCells.some(
                        (c) =>
                            c.hIndex <= hIndex &&
                            hIndex < c.hIndex + c.colSpan &&
                            c.vIndex <= vIndex &&
                            vIndex < c.vIndex + c.rowSpan,
                    );
                    if (!isInMerged) {
                        console.log('===表格粘贴异常: 这个位置的cell应该在合并单元格中', { hIndex, vIndex });
                        isDirty = true;
                    }
                }
            });
        });
        mergedCells.forEach((c) => {
            for (let i = c.vIndex; i < c.vIndex + c.rowSpan; i++) {
                for (let j = c.hIndex; j < c.hIndex + c.colSpan; j++) {
                    if (i === c.vIndex && j === c.hIndex) continue;
                    if (layer.data.cells[i][j] !== null) {
                        console.log('===表格粘贴异常: 合并单元格中不应该有其他cell, 应该都为null', c);
                        isDirty = true;
                    }
                }
            }
        });
        return isDirty;
    };

    if (checkLayerIsDirty(newLayer)) {
        console.log('===表格粘贴异常', {
            newLayer,
            clipboardLayer,
        });
        // 包含合并单元格的各种情况的粘贴，都可能会导致脏数据、表格布局错乱的bug，这个脏数据是不可修复的，会一直有问题.
        // 所以这里做一下简单的校验, 直接报错, 防止产生更糟糕的结果
        throw new Error('===表格粘贴异常');
    }

    console.log('===newLayer', cloneDeep(newLayer));

    newLayer.data.styles = getUsingStyles(newLayer);
    newLayer = await fixTableLayout({
        originalLayer: newLayer,
        stageScale: framework.stage.getScale(),
    });
    addOrUpdateLayer({
        frameworkIdentity,
        layer: newLayer,
        resetMaterialIds: true,
    });
    framework.operate.setActiveLayer(newLayer.id);
    framework.history.write(cloneDeep(targetLayer), newLayer);

    // 取消激活框
    getTableEditState(frameworkIdentity).initState();
};

export const addTableLayerByClipboard = async ({
    frameworkIdentity,
    clipboardText,
    clipboardHTML,
    addLayer,
}: {
    frameworkIdentity: string;
    clipboardText: string;
    clipboardHTML?: string;
    addLayer: (layer: IAddLayerArgs) => ILayerProps;
}) => {
    const { $t } = getLocaleStore();
    try {
        console.log('===addTableLayerByClipboard clipboardData', {
            clipboardHTML,
            clipboardText,
            customClipboardData: clipboardHTML ? getCustomClipboardData(clipboardHTML) : null,
        });
        const newLayer = await getTableLayerByClipboardData({
            frameworkIdentity,
            clipboardText,
            clipboardHTML,
            onExceed: () => {
                Message.info($t((v, { r }) => r(v.tableLayer.hooks.supportText, [MAX_ROW_COUNT, MAX_COL_COUNT])));
            },
        });
        getTableLayerScreenshotUrl(newLayer as any as ITableLayer, frameworkIdentity).then((url) => {
            newLayer.data.screenshotUrl = url;
            addLayer({
                layer: newLayer as any as ILayerProps,
                trackFunc: getTrackFunc(frameworkIdentity, newLayer as any as ILayerProps, 'addByImport'),
            });
        });
    } catch (error) {
        console.log('===表格解析失败', error);
        Message.info($t((v) => v.tableLayer.hooks.parseFailed));
    }
};
